import { useState, useEffect, useRef } from "react"

import "./PersonasExtraReserva.css"
import { Modal } from "src/shared/components/layout/modal/Modal"
import IconBorder from "src/shared/components/data-display/IconBorder/IconBorder"
import Icon from "src/shared/icons"
import Counter from "src/shared/components/forms/counter/Counter"
import { Controller, useForm } from "react-hook-form"
import Dropdown from "src/shared/components/forms/selelct-dropdown/Dropdown"
import { PrimaryButton } from "../../sections/elements/Elements"
import { InputText } from "src/shared/components/forms"
import { validateOnlyNumbers } from "src/shared/sections/payment/Payment.helpers"
import useMiniSnackbar from "src/shared/hooks/useMiniSnackbar"
import { InputCurrency } from "src/shared/sections/payment/InputCurrency"
import { useReserva } from "../../hooks/useReserva"
import { TipoDescuento, TiposPagos, useAgregarPersonaExtraReservaMutation, useCalcularMontoPersonaExtraQuery } from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { getCurrencyFormat } from "src/utils/string"
import LoaderComponent from "src/shared/components/layout/loader/Loader"
import { useStateDescuentos } from "src/shared/modals/ModalDescuentos/hooks/useStateDescuentos"
import { div, minus, times } from "src/shared/helpers/calculator"
import useAuth, { RoleNames } from "src/shared/hooks/useAuth"
import AuthRequiredModal from "src/pages/inventario/modals/Auth/AuthRequiredModal/AuthRequiredModal"
import DescuentoInput from "./Inputs/Descuento/DescuentoInput"
import { PAYMENT_TYPES } from "src/constants/payments"

type FormValues = {
    counter: number
    pagoParcial: string
    formaDePago: string
    cardNumber: string
    monto: number
    descuento: {
        descuento_id: string
        total: number
        nombre: string
        visible: boolean
    }
}

const PersonasExtraReserva = ({
    onClose,
    onConfirmed,
}: {
    onClose: (data?: any) => void
    onConfirmed?: () => void
}) => {
    const {
        control,
        handleSubmit,
        watch,
        getValues,
        reset,
        setValue,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            counter: 0,
            pagoParcial: "",
            formaDePago: "",
            cardNumber: "",
            monto: 0,
            descuento: {
                descuento_id: "",
                total: 0,
                nombre: "",
                visible: false,
            },
        },
    })

    const pagoParcial = watch("pagoParcial")
    const formaDePagoSelected = watch("formaDePago")
    const counterValue = watch("counter")

    const { hotel_id, usuario_id } = useProfile()
    const { selectedReservation } = useReserva()

    const { data: montoPersonaExtraData } = useCalcularMontoPersonaExtraQuery({
        variables: {
            input: {
                hotel_id,
                reserva_id: selectedReservation?.reserva_id,
            },
        },
        skip: !selectedReservation?.reserva_id,
    })

    const pricePerPerson = montoPersonaExtraData?.calcular_monto_persona_extra.monto_persona_extra ?? 0

    const [costoExtra, setCostoExtra] = useState(0)
    const [isSubmitLoading, setisSubmitLoading] = useState(false)
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
    const [descuentoMeta, setDescuentoMeta] = useState<{ valor: number; tipo_descuento: string } | null>(null)
    const usuarioAutorizoIdRef = useRef<string | null>(null)

    const personasExtraMax =
        (selectedReservation?.habitacion?.tipo_habitacion?.personas_extra_max ?? 0) -
        (selectedReservation?.personas_extras ?? 0)

    const { Modal: AuthModal } = useAuth({
        authModal: (
            <AuthRequiredModal
                authorizedPins={[RoleNames.admin, RoleNames.gerente, RoleNames.superadmin]}
                isOpen={isAuthModalOpen}
                onAuthFilled={(_value, _sampleData, userId) => {
                    usuarioAutorizoIdRef.current = userId || null
                    setIsAuthModalOpen(false)
                    handleSubmit(handleAddPerson)()
                }}
                onClose={() => setIsAuthModalOpen(false)}
            />
        ),
        noNeedAuthModalRoles: [],
        authorizedRoles: [
            RoleNames.admin,
            RoleNames.recepcionista,
            RoleNames.gerente,
            RoleNames.superadmin,
        ],
        isOpen: isAuthModalOpen,
        onClose: () => setIsAuthModalOpen(false),
    })

    const { descuento, setDescuento } = useStateDescuentos()

    useEffect(() => {
        if (!descuentoMeta || descuentoMeta.tipo_descuento !== TipoDescuento.Porcentaje) return
        const nuevoTotal = times(div(descuentoMeta.valor, 100), costoExtra)
        setDescuento(prev => ({ ...prev, total: nuevoTotal }))
    }, [costoExtra, descuentoMeta])

    const totalAPagar = minus(costoExtra, descuento.total)

    const { showMiniSnackbar: showSnackbar } = useMiniSnackbar()

    const [addPersonaExtra] = useAgregarPersonaExtraReservaMutation()

    const handleAddPerson = (data: FormValues) => {
        if (counterValue > personasExtraMax && personasExtraMax > 0 && !usuarioAutorizoIdRef.current) {
            setIsAuthModalOpen(true)
            return
        }

        onSubmit(data)
    }

    const onSubmit = (data: FormValues) => {
        if (!getValues().counter || !data.pagoParcial || isSubmitLoading) {
            return
        }
        setisSubmitLoading(true)
        const pago_personas = {
            detallesPago: [
                {
                    subtotal: pagoParcial === "total" ? totalAPagar : data.monto,
                    tipo_pago: data.formaDePago as TiposPagos,
                    ...(data.cardNumber ? { ultimos_digitos: data.cardNumber } : {}),
                },
            ],
            hotel_id,
            total: pagoParcial === "total" ? totalAPagar : data.monto,
            usuario_id,
        }
        addPersonaExtra({
            variables: {
                add_extras_reserva_input: {
                    personas_extras_plus: data.counter,
                    reserva_id: selectedReservation?.reserva_id || "",
                    usuario_id: usuario_id,
                    pago_personas: data?.pagoParcial !== "pendiente" ? pago_personas : undefined,
                    descuento_id: descuento.descuento_id || null,
                    ...(usuarioAutorizoIdRef.current ? { usuario_autorizo_id: usuarioAutorizoIdRef.current } : {}),
                },
            },
            onCompleted: (data) => {
                showSnackbar({
                    title: "Personas extras agregadas",
                    status: "success",
                    text: `Se agregaron **${getValues().counter}** personas extras a la reserva **${
                        (selectedReservation as any)?.codigo_ota
                            ? (selectedReservation as any)?.codigo_ota
                            : `ER-${(selectedReservation as any)?.folio.toString().padStart(3, "0")}`
                    }** exitosamente. El ajuste en el monto total se reflejará en el detalle de la reserva.`,
                })
                onConfirmed?.()
                onClose?.(data)
                setisSubmitLoading(false)
                usuarioAutorizoIdRef.current = null
            },
            onError: (data) => {
                showSnackbar({
                    title: "Error al agregar personas extra",
                    status: "error",
                    text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente",
                })
                onClose?.(data)
                setisSubmitLoading(false)
                usuarioAutorizoIdRef.current = null
            },
        })
    }

    return (
        <Modal
            height={"fit-content"}
            width={572}
            withCloseButton
            isOpen={true}
            isCancelableOnClickOutside={false}
            onClose={() => onClose?.()}
            className="persona-extra-container-modal-container"
        >
            <form className="modal__personas-extra--reserva__container" onSubmit={handleSubmit(onSubmit)}>
                <IconBorder primaryBgColor="var(--fondo-close)" primaryBgDiameter={60}>
                    <Icon name="UserParentFill" width={30} height={30} color="var(--purple-drawer-primario)" />
                </IconBorder>
                <span className="modal__personas-extra--reserva__title">Agregar personas extra</span>
                <Controller
                    name="counter"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field: { onChange, value } }) => (
                        <Counter
                            onClick={(value) => {
                                onChange(value)
                                if (value < 1) {
                                    reset()
                                    setDescuento({ descuento_id: "", total: 0, nombre: "", visible: false })
                                    setDescuentoMeta(null)
                                }
                                setCostoExtra((value * (pricePerPerson ?? 0)))
                            }}
                            disable={false}
                            value={getValues().counter}
                            errorHintText={
                                counterValue > personasExtraMax && personasExtraMax > 0
                                    ? "Límite de personas extra superado, necesitarás PIN para vender"
                                    : ""
                            }
                        />
                    )}
                />
                <div className="modal__personas-extra--reserva__costos">
                    <span className="modal__personas-extra--reserva__label">
                        {"Total a pagar"}
                    </span>
                    <span className="modal__personas-extra--reserva__value">
                        {formatCurrency(totalAPagar)}
                    </span>
                </div>
                {errors.counter && <div className="error-text">Agrega una cantidad</div>}
                <div className={"modal__personas-extra--reserva__pago-row"}>
                    <Controller
                        name="pagoParcial"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                            <Dropdown
                                icon="dollarCircle"
                                value={value}
                                options={[
                                    { value: "parcial", label: "Parcial" },
                                    { value: "total", label: "Total" },
                                    { value: "pendiente", label: "Pendiente" },
                                ]}
                                placeholder="Selecciona el pago"
                                onClick={(data) => {
                                    onChange(data.value)
                                    setValue("formaDePago", "")
                                }}
                                label={"Pago"}
                                iconInOptions={false}
                                errorHintText={errors.pagoParcial ? "Selecciona un método de pago" : ""}
                            />
                        )}
                    />
                    <DescuentoInput
                        filter={{
                            type: "reservas",
                            total: costoExtra,
                            tipo_habitacion_ids: selectedReservation?.tipo_habitacion_id
                                ? [selectedReservation.tipo_habitacion_id]
                                : undefined,
                            fecha_inicio: selectedReservation?.fecha_entrada,
                            fecha_fin: selectedReservation?.fecha_salida,
                            unique_discount_ids: selectedReservation?.descuento_aplicado?.descuento_id
                                ? [selectedReservation.descuento_aplicado.descuento_id]
                                : [],
                        }}
                        value={descuento.descuento_id}
                        onChange={(v) => {
                            setDescuentoMeta(
                                v.descuento_id && v.tipo_descuento
                                    ? { valor: v.valor ?? 0, tipo_descuento: v.tipo_descuento }
                                    : null
                            )
                            setDescuento({
                                descuento_id: v.descuento_id,
                                total: v.total,
                                nombre: v.nombre,
                                visible: false,
                                tipo_descuento: v?.tipo_descuento as string | undefined,
                                valor: v?.valor !== undefined ? Number(v.valor) : undefined,
                            })
                        }}
                        disabled={ pagoParcial !== PAYMENT_TYPES.total}
                    />
                </div>
                {pagoParcial && pagoParcial !== "pendiente" ? (
                    <Controller
                        name="formaDePago"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                            <Dropdown
                                value={value}
                                icon="creditCard"
                                options={[
                                    { label: "Efectivo", value: TiposPagos.Efectivo },
                                    { label: "Visa o Mastercard", value: TiposPagos.VisaOMastercard },
                                    { label: "AMEX", value: TiposPagos.Amex },
                                    { label: "Depósito/Transfer", value: TiposPagos.DepositoOTransferencia },
                                    // no hay pago pendiente para madero
                                    // { label: "Pago pendiente", value: "pendiente" },
                                ]}
                                iconInOptions={false}
                                placeholder="Selecciona el pago"
                                onClick={(data) => onChange(data.value)}
                                label={"Forma de pago"}
                                errorHintText={errors.formaDePago ? "Selecciona un método de pago" : ""}
                            />
                        )}
                    />
                ) : null}
                {formaDePagoSelected &&
                formaDePagoSelected !== TiposPagos.Cortesia &&
                formaDePagoSelected !== TiposPagos.Efectivo &&
                formaDePagoSelected !== "pendiente" &&
                formaDePagoSelected !== TiposPagos.ConsumoInterno ? (
                    <Controller
                        control={control}
                        name={"cardNumber"}
                        rules={{ required: true, minLength: 4 }}
                        render={({ field: { value, onChange }, formState: { errors } }) => (
                            <InputText
                                className={"modal__personas-extra-reserva__card-number"}
                                label={"Número de tarjeta o referencia"}
                                type={"text"}
                                placeholder={"Máximo 10 dígitos"}
                                errorhinttext={"Ingresa mínimo 4 dígitos"}
                                error={!!errors?.cardNumber}
                                value={value}
                                icon={Icon}
                                iconProps={{
                                    name: "iconHash",
                                    color: "var(--header-dark)",
                                    height: 16,
                                    width: 16,
                                }}
                                onChange={(e) => {
                                    const value = e.target.value
                                    if (validateOnlyNumbers(value, 10)) onChange(value)
                                }}
                            />
                        )}
                    />
                ) : null}
                {pagoParcial === "parcial" && formaDePagoSelected ? (
                    <Controller
                        control={control}
                        name={"monto"}
                        rules={{ required: true, min: 1, max: totalAPagar }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <InputCurrency
                                errorhinttext={
                                    error?.type === "max"
                                        ? `Ingresa un monto menor a ${getCurrencyFormat(totalAPagar)}`
                                        : error?.type === "required"
                                        ? "Ingresa un monto"
                                        : ""
                                }
                                error={error ? true : false}
                                label="Monto"
                                placeholder="Escribe un monto"
                                onChange={(value) => onChange(value)}
                                value={value}
                                whiteSpace={true}
                                limit={totalAPagar}
                            />
                        )}
                    />
                ) : null}
                <PrimaryButton
                    type="button"
                    text="Agregar persona extra"
                    className="persona-extra-modal-button"
                    disabled={isSubmitLoading}
                    onClick={handleSubmit(handleAddPerson)}
                />
                <LoaderComponent visible={isSubmitLoading} />
            </form>
            {AuthModal}
        </Modal>
    )
}

export default PersonasExtraReserva
